import { Search } from '@mui/icons-material';
import {
  Box, Button, Chip, FormControl, Grid, IconButton, InputLabel, MenuItem, Select, SelectChangeEvent, Stack,
  SvgIcon, Table, TableBody, TableCell, TableHead, TableRow, TableContainer, TextField, Typography, useMediaQuery, useTheme,
} from '@mui/material';
import { useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Plus, Trash01 } from '../../../assets';
import { itemTagsClient, ItemTagDto, ItemTagInstanceOption, ItemTagMarketOption } from '../../../app/services/api/itemTags';
import { client } from '../../../app/services/api/orderManagementClient';
import MessagesSnackbar from '../../../components/shared/MessagesSnackbar';
import PageArea from '../../../components/shared/PageArea';
import PageTitle from '../../../components/shared/PageTitle';
import { ConfirmationDialog } from '../../../components/shared/dialogs/ConfirmationDialog';
import { EmptyTable } from '../../../components/shared/EmptyTable';
import { Pill } from '../../../components/shared/Pill';
import { UserContext } from '../../../components/shared/useUser';
import CustomPagination from '../../../components/forms/Pagination';
import { TableLoading } from '../../../components/forms/TableLoading';
import { Section } from '../../../components/ui/Section';
import { ItemTagChip } from './components/ItemTagChip';

const ITEM_TAGS_PAGE_SIZE = 10;
const ITEM_TAGS_HEADER_HEIGHT = 45;
const ITEM_TAGS_ROW_HEIGHT = 56;

const ManageItemTags = () => {
  const { t } = useTranslation('itemTags');
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(`(max-width:${theme.breakpoints.values.md}px)`);
  const { hasPermissionTo, selectedFranchise, selectedStore } = useContext(UserContext);

  const [itemTags, setItemTags] = useState<ItemTagDto[]>([]);
  const [instances, setInstances] = useState<ItemTagInstanceOption[]>([]);
  const [markets, setMarkets] = useState<ItemTagMarketOption[]>([]);
  const [searchInput, setSearchInput] = useState('');
  const [searchText, setSearchText] = useState('');
  const [selectedInstance, setSelectedInstance] = useState('');
  const [selectedMarket, setSelectedMarket] = useState('');
  const [loggedInCompanyId, setLoggedInCompanyId] = useState<number | null>(null);
  const [isResolvingLoggedInCompanyId, setIsResolvingLoggedInCompanyId] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState('active');
  const [currentPage, setCurrentPage] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteCandidate, setDeleteCandidate] = useState<ItemTagDto | null>(null);
  const [feedback, setFeedback] = useState<{ message: string; severity: 'success' | 'error' } | null>(null);

  const canViewItemTags = hasPermissionTo(['ItemTagView']);
  const canCreateItemTags = hasPermissionTo(['ItemTagCreate']);
  const canUpdateItemTags = hasPermissionTo(['ItemTagUpdate']);
  const canAssignItemTags = hasPermissionTo(['ItemTagAssign']);
  const canDeleteItemTags = hasPermissionTo(['ItemTagDelete']);
  
  const canOpenItemTag = canUpdateItemTags || canAssignItemTags;
  const showActionsColumn = canDeleteItemTags;
  const maxPages = Math.max(Math.ceil(itemTags.length / ITEM_TAGS_PAGE_SIZE), 1);
  const hasScopeSelection = Boolean(selectedInstance && selectedMarket);
  const handleCreateNew = () => navigate('/item-tags/create');

  const resolveDefaultInstance = (availableInstances: ItemTagInstanceOption[]) => {
    if (availableInstances.length === 0) {
      return '';
    }

    if (loggedInCompanyId && availableInstances.some((instance) => instance.id === loggedInCompanyId)) {
      return loggedInCompanyId.toString();
    }

    return '';
  };

  const resolveDefaultMarket = (availableMarkets: ItemTagMarketOption[]) => {
    if (availableMarkets.length === 0) {
      return '';
    }

    const preferredFranchiseName = selectedFranchise?.franchiseName?.trim().toLowerCase();
    if (!preferredFranchiseName) {
      return '';
    }

    const marketMatch = availableMarkets.find((market) => market.name.trim().toLowerCase() === preferredFranchiseName);
    return marketMatch ? marketMatch.id.toString() : '';
  };

  const visibleItemTags = useMemo(
    () => itemTags.slice(currentPage * ITEM_TAGS_PAGE_SIZE, (currentPage + 1) * ITEM_TAGS_PAGE_SIZE),
    [currentPage, itemTags],
  );

  const loadInstances = async () => {
    const result = await client.getInstances({
      name: '',
      sort: 'name',
      direction: 'asc',
      pageNumber: 0,
      pageSize: 200,
    });
    setInstances((result.data?.items ?? []).map((instance): ItemTagInstanceOption => ({
      id: instance.id,
      name: instance.name,
    })));
  };

  const loadMarkets = async (companyId?: number) => {
    const result = await client.getMarkets({
      companyId: companyId ? companyId.toString() : '',
      name: '',
      sort: 'name',
      direction: 'asc',
      pageNumber: 0,
      pageSize: 500,
    });
    setMarkets((result.data?.items ?? []).map((market): ItemTagMarketOption => ({
      id: market.customerTypeId,
      companyId: market.companyId,
      name: market.name,
      instanceName: market.instanceName,
    })));
  };

  const loadItemTags = async () => {
    if (!selectedInstance || !selectedMarket) {
      setItemTags([]);
      return;
    }

    setIsLoading(true);

    try {
      const nextTags = await itemTagsClient.getItemTags({
        searchText: searchText || undefined,
        companyId: Number(selectedInstance),
        customerTypeId: Number(selectedMarket),
        isActive: selectedStatus === '' ? undefined : selectedStatus === 'active',
      });
      setItemTags(nextTags);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    let isActive = true;

    const storeNumber = selectedStore?.storeNumber?.trim();
    if (!storeNumber) {
      setLoggedInCompanyId(null);
      setIsResolvingLoggedInCompanyId(false);
      return () => {
        isActive = false;
      };
    }

    setIsResolvingLoggedInCompanyId(true);

    const loadLoggedInCompanyId = async () => {
      try {
        const result = await client.getCompanyIdByStoreNumber(storeNumber);

        if (isActive) {
          setLoggedInCompanyId(result.success ? result.data : null);
        }
      } catch {
        if (isActive) {
          setLoggedInCompanyId(null);
        }
      } finally {
        if (isActive) {
          setIsResolvingLoggedInCompanyId(false);
        }
      }
    };

    loadLoggedInCompanyId();

    return () => {
      isActive = false;
    };
  }, [selectedStore?.storeNumber]);

  useEffect(() => {
    loadInstances();
  }, []);

  useEffect(() => {
    if (!selectedInstance) {
      setMarkets([]);
      setSelectedMarket('');
      return;
    }

    loadMarkets(Number(selectedInstance));
  }, [selectedInstance]);

  useEffect(() => {
    if (selectedInstance || instances.length === 0 || isResolvingLoggedInCompanyId) {
      return;
    }

    const defaultInstance = resolveDefaultInstance(instances);
    if (defaultInstance) {
      setSelectedInstance(defaultInstance);
    }
  }, [instances, isResolvingLoggedInCompanyId, loggedInCompanyId, selectedInstance]);

  useEffect(() => {
    if (!selectedInstance || selectedMarket) {
      return;
    }

    const defaultMarket = resolveDefaultMarket(markets);
    if (defaultMarket) {
      setSelectedMarket(defaultMarket);
    }
  }, [markets, selectedFranchise?.franchiseName, selectedInstance, selectedMarket]);

  useEffect(() => {
    loadItemTags();
  }, [searchText, selectedInstance, selectedMarket, selectedStatus]);

  useEffect(() => {
    setCurrentPage(0);
  }, [searchText, selectedInstance, selectedMarket, selectedStatus]);

  useEffect(() => {
    const lastAvailablePage = Math.max(Math.ceil(itemTags.length / ITEM_TAGS_PAGE_SIZE) - 1, 0);

    if (currentPage > lastAvailablePage) {
      setCurrentPage(lastAvailablePage);
    }
  }, [currentPage, itemTags.length]);

  const handleSearch = () => {
    setSearchText(searchInput.trim());
  };

  const handleClear = () => {
    setSearchInput('');
    setSearchText('');
    setSelectedInstance(isResolvingLoggedInCompanyId ? '' : resolveDefaultInstance(instances));
    setSelectedMarket('');
    setSelectedStatus('active');
  };

  const handleInstanceChange = (event: SelectChangeEvent<string>) => {
    setSelectedInstance(event.target.value);
    setSelectedMarket('');
  };

  const handleMarketChange = (event: SelectChangeEvent<string>) => {
    setSelectedMarket(event.target.value);
  };

  const handleDelete = async () => {
    if (!deleteCandidate) {
      return;
    }

    setIsDeleting(true);

    try {
      await itemTagsClient.deleteItemTag(deleteCandidate.id);
      setDeleteCandidate(null);
      setFeedback({ message: t('deleteSuccessMessage'), severity: 'success' });
      await loadItemTags();
    } catch (error) {
      setFeedback({
        message: error instanceof Error ? error.message : t('deleteErrorMessage'),
        severity: 'error',
      });
    } finally {
      setIsDeleting(false);
    }
  };

  const handleRowOpen = (itemTag: ItemTagDto) => {
    if (canUpdateItemTags) {
      navigate(`/item-tags/edit/${itemTag.id}`);
      return;
    }

    if (canAssignItemTags) {
      navigate(`/item-tags/${itemTag.id}/items#assignment`);
    }
  };

  const formatDate = (value: string) => new Date(value).toLocaleDateString();

  if (!canViewItemTags) {
    return null;
  }

  return (
    <PageArea>
      <Stack
        width='100%'
        spacing={2}
        direction={isMobile ? 'column' : 'row'}
        sx={{
          justifyContent: 'space-between',
          mb: 4,
        }}
      >
        <Box width={isMobile ? '100%' : 'auto'}>
          <PageTitle subTitle={t('pageSubtitle')}>{t('pageTitle')}</PageTitle>
        </Box>

        {canCreateItemTags && (
          <Box
            width={isMobile ? '100%' : 'auto'}
            sx={{
              display: 'flex',
              justifyContent: { xs: 'flex-start', md: 'flex-end' },
            }}
          >
            <Button
              variant='primary'
              size='lg'
              onClick={handleCreateNew}
              startIcon={<SvgIcon><Plus fill='white' /></SvgIcon>}
              sx={{
                mb: { xs: 4, md: 0 },
              }}
            >
              {t('createTag')}
            </Button>
          </Box>
        )}
      </Stack>

      <Grid
        container
        spacing={4}
        sx={{ mb: 4 }}
      >
        <Grid
          item
          lg={2.5}
          md={6}
          xs={12}
        >
          <TextField
            label={t('search')}
            placeholder={t('searchPlaceholder')}
            value={searchInput}
            onChange={(event) => setSearchInput(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                handleSearch();
              }
            }}
            disabled={!hasScopeSelection}
            fullWidth
          />
        </Grid>
        <Grid
          item
          lg={2.5}
          md={6}
          xs={12}
        >
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByInstance')}</InputLabel>
            <Select
              value={selectedInstance}
              onChange={handleInstanceChange}
              label={t('filterByInstance')}
              displayEmpty
              notched
            >
              <MenuItem value=''><em>{t('selectInstance')}</em></MenuItem>
              {instances.map((instance) => (
                <MenuItem
                  key={instance.id}
                  value={instance.id.toString()}
                >
                  {instance.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          lg={2.5}
          md={6}
          xs={12}
        >
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByFranchise')}</InputLabel>
            <Select
              value={selectedMarket}
              onChange={handleMarketChange}
              label={t('filterByFranchise')}
              displayEmpty
              notched
              disabled={!selectedInstance}
            >
              <MenuItem value=''><em>{t('selectFranchise')}</em></MenuItem>
              {markets.map((market) => (
                <MenuItem
                  key={market.id}
                  value={market.id.toString()}
                >
                  {market.name}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          lg={2}
          md={6}
          xs={12}
        >
          <FormControl fullWidth>
            <InputLabel shrink>{t('filterByStatus')}</InputLabel>
            <Select
              value={selectedStatus}
              onChange={(event: SelectChangeEvent<string>) => setSelectedStatus(event.target.value)}
              label={t('filterByStatus')}
              displayEmpty
              notched
            >
              <MenuItem value=''>{t('all')}</MenuItem>
              <MenuItem value='active'>{t('activeOnly')}</MenuItem>
              <MenuItem value='inactive'>{t('inactiveOnly')}</MenuItem>
            </Select>
          </FormControl>
        </Grid>
        <Grid
          item
          lg={1.25}
          md={3}
          xs={6}
        >
          <Button
            fullWidth
            variant='secondary'
            size='lg'
            onClick={handleClear}
          >
            {t('clear')}
          </Button>
        </Grid>
        <Grid
          item
          lg={1.25}
          md={3}
          xs={6}
        >
          <Button
            fullWidth
            size='lg'
            onClick={handleSearch}
            startIcon={<SvgIcon><Search /></SvgIcon>}
            disabled={!hasScopeSelection}
          >
            {t('search')}
          </Button>
        </Grid>
      </Grid>

      <Section>
        {isLoading ? (
          <TableLoading
            headerHeight={ITEM_TAGS_HEADER_HEIGHT}
            bodyRowHeight={ITEM_TAGS_ROW_HEIGHT}
            minTableWidth={920}
          />
        ) : !hasScopeSelection ? (
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              minHeight: 360,
              p: 4,
              textAlign: 'center',
            }}
          >
            <Typography variant='textMD'
              color='text.secondary'>
              {t('selectInstanceAndFranchiseFirst')}
            </Typography>
          </Box>
        ) : itemTags.length === 0 ? (
          <EmptyTable
            text={t('pageTitle')}
            searchTerm={searchText}
            onClear={handleClear}
            onAdd={handleCreateNew}
            addItemText={t('createTag')}
            onAddIsCreating={false}
            onAddIsDisabled={!canCreateItemTags}
            headerHeight={ITEM_TAGS_HEADER_HEIGHT}
            bodyRowHeight={ITEM_TAGS_ROW_HEIGHT}
            showOnAdd={canCreateItemTags}
            withPaginationShown={false}
          />
        ) : (
          <>
            <Box
              sx={{
                p: 5,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: { xs: 'flex-start', md: 'center' },
                gap: 2,
                flexDirection: { xs: 'column', md: 'row' },
                borderBottom: '1px solid',
                borderColor: 'divider',
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                <Typography
                  variant='textMD'
                  fontWeight={600}
                >
                  {t('pageTitle')}
                </Typography>
                <Pill text={`${itemTags.length} ${t('pageTitle')}`} />
              </Box>
            </Box>
            <TableContainer
              component='div'
              sx={{
                overflowX: 'auto',
              }}
            >
              <Table size='small'
                aria-label='item tags table'>
                <TableHead
                  sx={{ backgroundColor: theme.palette.custom.gray[100] }}>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>{t('preview')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('instance')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('market')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('status')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('assignedItems')}</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>{t('updated')}</TableCell>
                    {showActionsColumn && <TableCell sx={{ fontWeight: 600 }}>{t('actions')}</TableCell>}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {visibleItemTags.map((itemTag) => (
                    <TableRow
                      key={itemTag.id}
                      hover={canOpenItemTag}
                      onClick={() => canOpenItemTag && handleRowOpen(itemTag)}
                      onKeyDown={(event) => {
                        if (!canOpenItemTag) {
                          return;
                        }

                        if (event.key === 'Enter' || event.key === ' ') {
                          event.preventDefault();
                          handleRowOpen(itemTag);
                        }
                      }}
                      tabIndex={canOpenItemTag ? 0 : -1}
                      sx={{
                        height: ITEM_TAGS_ROW_HEIGHT,
                        opacity: itemTag.isActive ? 1 : 0.75,
                        cursor: canOpenItemTag ? 'pointer' : 'default',
                      }}>
                      <TableCell>
                        <ItemTagChip
                          name={itemTag.name}
                          textColor={itemTag.textColor}
                          backgroundColor={itemTag.backgroundColor}
                          iconKey={itemTag.iconKey}
                          isActive={itemTag.isActive}
                          placeholderLabel={t('previewFallbackName')}
                        />
                      </TableCell>
                      <TableCell>{itemTag.instanceName}</TableCell>
                      <TableCell>{itemTag.marketName}</TableCell>
                      <TableCell>
                        <Chip
                          label={itemTag.isActive ? t('activeLabel') : t('inactiveLabel')}
                          color={itemTag.isActive ? 'success' : 'default'}
                          size='small'
                        />
                      </TableCell>
                      <TableCell>{itemTag.assignedItemCount}</TableCell>
                      <TableCell>{formatDate(itemTag.updatedAtUtc)}</TableCell>
                      {showActionsColumn && (
                        <TableCell>
                          <IconButton
                            aria-label={t('delete')}
                            size='small'
                            onClick={(event) => {
                              event.stopPropagation();
                              setDeleteCandidate(itemTag);
                            }}>
                            <SvgIcon style={{ color: theme.palette.error[500] }}><Trash01 /></SvgIcon>
                          </IconButton>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <Box sx={{ borderTop: '1px solid', borderColor: 'divider' }}>
              <CustomPagination
                page={currentPage}
                setPage={setCurrentPage}
                maxPages={maxPages}
                breakpointForChangeDisplay={120000}
                useNoDataMessage={false}
                disabled={itemTags.length <= ITEM_TAGS_PAGE_SIZE}
              />
            </Box>
          </>
        )}
      </Section>

      <ConfirmationDialog
        open={Boolean(deleteCandidate)}
        title={t('deleteConfirmTitle')}
        message={t('deleteConfirmMessage', { tagName: deleteCandidate?.name || '' })}
        icon={<Trash01 />}
        variant='bad'
        confirmButtonText={isDeleting ? t('deleting') : t('delete')}
        cancelButtonText={t('cancel')}
        isProcessing={isDeleting}
        onCancel={() => setDeleteCandidate(null)}
        onConfirm={handleDelete}
      />

      <MessagesSnackbar
        open={Boolean(feedback)}
        onClose={() => setFeedback(null)}
        message={feedback?.message || ''}
        severity={feedback?.severity || 'success'}
        duration={3000}
      />
    </PageArea>
  );
};

export default ManageItemTags;